import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import posthog from "posthog-js";

export const checkAuthenticated = createAsyncThunk(
  "user/checkAuthenticated",
  async () => {
    const res = await axios.get(`/api/account`);
    return res.data;
  }
);

export const logoutUser = createAsyncThunk("user/logout", async () => {
  const config = {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  };

  const body = JSON.stringify({
    withCredentials: true,
  });
  const res = await axios.post("/api/logout", body, config);
  return res.data;
});

export const updateUserProfile = createAsyncThunk(
  "user/updateUserProfile",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "/api/user/update",
        {
          first_name: userData.first_name,
          user_location: userData.user_location,
          user_address: userData.user_address,
        },
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteAccount = createAsyncThunk(
  "user/deleteAccount",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "/api/account/delete",
        {},
        {
          withCredentials: true,
        }
      );
      localStorage.removeItem("token");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const completePhoneSignup = createAsyncThunk(
  "user/completePhoneSignup",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "/api/signup/phone/complete/",
        userData,
        {
          withCredentials: true,
        }
      );
      // Store signup timestamp when signup is successful
      localStorage.setItem("signupTimestamp", Date.now().toString());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const loginUser = createAsyncThunk(
  "user/login",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/login/verify/", userData, {
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const googleLogin = createAsyncThunk(
  "user/googleLogin",
  async (credential, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "/auth/staff/google/",
        {
          access_token: credential,
          id_token: credential,
        },
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const userSlice = createSlice({
  name: "user",
  initialState: {
    isAuthenticated: null,
    id: "",
    fullName: "",
    firstName: "",
    email: "",
    phoneNumber: "",
    userAddress: "",
    userLocation: null,
    zipCode: "",
    interestedIn: "",
    focusAreas: [],
    level: "",
    totalXp: 0,
    openSettingsDialog: false,
    settings: {},
    updateStatus: "idle",
    updateError: null,
    deleteStatus: "idle",
    deleteError: null,
    signupStatus: "idle",
    signupError: null,
    loginStatus: "idle",
    loginError: null,
    permissions: {
      can_label_data: false,
      can_clean_data: false,
      can_use_custom_messages: false,
    },
  },
  reducers: {
    setOpenSettingsDialog: (state, action) => {
      state.openSettingsDialog = action.payload;
    },
    setUserName: (state, action) => {
      const { name, first_name } = action.payload;
      state.fullName = name;
      state.firstName = first_name;
    },
    setUserSettings: (state, action) => {
      state.settings = action.payload;
    },
    updateUser: (state, action) => {
      const updatedUserData = action.payload;
      Object.assign(state, updatedUserData);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkAuthenticated.fulfilled, (state, action) => {
        if (action.payload.isAuthenticated) {
          state.isAuthenticated = true;
          state.id = action.payload.id;
          state.fullName = action.payload.name;
          state.firstName = action.payload.first_name;
          state.email = action.payload.email;
          state.settings = action.payload.settings;
          state.phoneNumber = action.payload.phone_number;
          state.userAddress = action.payload.user_address;
          state.userLocation = action.payload.user_location;
          state.level = action.payload.level;
          state.totalXp = action.payload.total_xp;
          state.interestedIn = action.payload.interested_in;
          state.focusAreas = action.payload.focus_areas;
          state.permissions = action.payload.permissions || {
            can_label_data: false,
            can_clean_data: false,
            can_use_custom_messages: false,
          };

          posthog.identify(action.payload.id, {
            first_name: action.payload.first_name,
            phone_number: action.payload.phone_number,
          });
        } else {
          state.isAuthenticated = false;
          state.id = "";
          state.fullName = "";
          state.firstName = "";
          state.email = "";
          state.settings = {};
          state.phoneNumber = "";
          state.zipCode = "";
          state.userAddress = "";
          state.userLocation = null;
          state.level = "";
          state.totalXp = 0;
          state.interestedIn = "";
          state.focusAreas = [];
          state.permissions = {
            can_label_data: false,
            can_clean_data: false,
            can_use_custom_messages: false,
          };

          posthog.reset();
        }
      })
      .addCase(checkAuthenticated.rejected, (state, action) => {
        state.isAuthenticated = false;
        state.id = "";
        state.fullName = "";
        state.firstName = "";
        state.email = "";
        state.settings = {};
        state.phoneNumber = "";
        state.zipCode = "";
        state.userAddress = "";
        state.userLocation = null;
        state.level = "";
        state.totalXp = 0;
        state.interestedIn = "";
        state.focusAreas = [];
        state.permissions = {
          can_label_data: false,
          can_clean_data: false,
          can_use_custom_messages: false,
        };
      })
      .addCase(logoutUser.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.isAuthenticated = false;
          state.id = "";
          state.fullName = "";
          state.firstName = "";
          state.email = "";
          state.settings = {};
          state.phoneNumber = "";
          state.zipCode = "";
          state.userAddress = "";
          state.userLocation = null;
          state.level = "";
          state.totalXp = 0;
          state.interestedIn = "";
          state.focusAreas = [];
          state.permissions = {
            can_label_data: false,
            can_clean_data: false,
            can_use_custom_messages: false,
          };

          posthog.reset();
        }
      })
      .addCase(updateUserProfile.pending, (state) => {
        state.updateStatus = "loading";
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.firstName = action.payload.first_name;
        state.userLocation = action.payload.user_location;
        state.userAddress = action.payload.user_address;
        state.updateStatus = "succeeded";
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.updateError =
          action.payload?.detail ||
          action.error.message ||
          "Failed to update profile";
      })
      .addCase(deleteAccount.pending, (state) => {
        state.deleteStatus = "loading";
      })
      .addCase(deleteAccount.fulfilled, (state) => {
        state.deleteStatus = "succeeded";
        // Reset user state and log out
        state.isAuthenticated = false;
        state.id = "";
        state.fullName = "";
        state.firstName = "";
        state.email = "";
        state.settings = {};
        state.phoneNumber = "";
        state.zipCode = "";
        state.userAddress = "";
        state.userLocation = null;
        state.level = "";
        state.totalXp = 0;
        state.interestedIn = "";
        state.focusAreas = [];
        state.permissions = {
          can_label_data: false,
          can_clean_data: false,
          can_use_custom_messages: false,
        };
      })

      .addCase(deleteAccount.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.deleteError = action.payload;
      })
      .addCase(completePhoneSignup.pending, (state) => {
        state.signupStatus = "loading";
      })
      .addCase(completePhoneSignup.fulfilled, (state, action) => {
        state.signupStatus = "succeeded";
        state.isAuthenticated = true;
        state.id = action.payload.id;
        state.fullName = action.payload.name;
        state.firstName = action.payload.first_name;
        state.phoneNumber = action.payload.phone_number;
        state.level = action.payload.level;
        state.totalXp = action.payload.total_xp;
        state.focusAreas = action.payload.focus_areas;
        state.userAddress = action.payload.user_address;
        state.userLocation = action.payload.user_location;
        state.interestedIn = action.payload.interested_in;
      })
      .addCase(completePhoneSignup.rejected, (state, action) => {
        state.signupStatus = "failed";
        state.signupError = action.payload;
      })
      .addCase(loginUser.pending, (state) => {
        state.loginStatus = "loading";
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loginStatus = "succeeded";
        state.isAuthenticated = true;
        state.id = action.payload.id;
        state.fullName = action.payload.name;
        state.firstName = action.payload.first_name;
        state.phoneNumber = action.payload.phone_number;
        state.level = action.payload.level;
        state.totalXp = action.payload.total_xp;
        state.focusAreas = action.payload.focus_areas;

        posthog.identify(action.payload.id, {
          first_name: action.payload.first_name,
          phone_number: action.payload.phone_number,
        });
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loginStatus = "failed";
        state.loginError = action.payload?.error || "Login failed";
      })
      .addCase(googleLogin.pending, (state) => {
        state.loginStatus = "loading";
      })
      .addCase(googleLogin.fulfilled, (state, action) => {
        state.loginStatus = "succeeded";
        if (action.payload.isAuthenticated) {
          state.isAuthenticated = true;
          state.id = action.payload.id;
          state.fullName = action.payload.name;
          state.firstName = action.payload.first_name;
          state.email = action.payload.email;
          state.phoneNumber = action.payload.phone_number;
          state.level = action.payload.level;
          state.totalXp = action.payload.total_xp;
          state.focusAreas = action.payload.focus_areas;
          state.permissions = action.payload.permissions || {
            can_label_data: false,
            can_clean_data: false,
            can_use_custom_messages: false,
          };

          posthog.identify(action.payload.id, {
            first_name: action.payload.first_name,
            email: action.payload.email,
            phone_number: action.payload.phone_number,
          });
        }
      })
      .addCase(googleLogin.rejected, (state, action) => {
        state.loginStatus = "failed";
        state.loginError = action.payload?.error || "Google login failed";
      });
  },
});

export const {
  setOpenSettingsDialog,
  setUserName,
  setUserSettings,
  updateUser,
} = userSlice.actions;

export default userSlice.reducer;
